import { _ as _async_to_generator } from "@swc/helpers/_/_async_to_generator";
import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { _ as _object_without_properties } from "@swc/helpers/_/_object_without_properties";
import { _ as _ts_generator } from "@swc/helpers/_/_ts_generator";
import { getGlobalProperty } from "@faire/web--source/common/globals/getGlobalProperty";
import { getWindowOrThrow } from "@faire/web--source/common/globals/getWindow";
import { trackFrontendApisPrefetchUnusedView } from "@faire/web-api--source/events/frontendApis/view/prefetchUnused";
import clone from "lodash/clone";
import { delay } from "../delay";
import { logError, logWarning } from "../logging";
import { isSameRequest } from "./__internal__/isSameRequest";
export var PREFETCH_UNUSED_TIMEOUT_MILLIS = 10000;
/**
 * Invoke the given request early, with the expectation that it'll be leveraged in the near future
 * by an outgoing request with identical options.
 *
 * Note that request interceptors are applied to the given config.
 */ export var prefetch = /*#__PURE__*/ function() {
    var _ref = _async_to_generator(function(requestOptions) {
        var route, beforePrefetchStartPerformanceMarker, afterPrefetchDonePerformanceMarker, otherConfig, config, interceptors, _iteratorNormalCompletion, _didIteratorError, _iteratorError, _iterator, _step, interceptor, err, prefetches, handler, prefetch;
        return _ts_generator(this, function(_state) {
            switch(_state.label){
                case 0:
                    route = requestOptions.route, beforePrefetchStartPerformanceMarker = requestOptions.beforePrefetchStartPerformanceMarker, afterPrefetchDonePerformanceMarker = requestOptions.afterPrefetchDonePerformanceMarker, otherConfig = _object_without_properties(requestOptions, [
                        "route",
                        "beforePrefetchStartPerformanceMarker",
                        "afterPrefetchDonePerformanceMarker"
                    ]);
                    config = clone(_object_spread({}, otherConfig));
                    interceptors = getGlobalProperty("faire.api.requestInterceptors");
                    if (!interceptors) return [
                        3,
                        8
                    ];
                    _iteratorNormalCompletion = true, _didIteratorError = false, _iteratorError = undefined;
                    _state.label = 1;
                case 1:
                    _state.trys.push([
                        1,
                        6,
                        7,
                        8
                    ]);
                    _iterator = interceptors[Symbol.iterator]();
                    _state.label = 2;
                case 2:
                    if (!!(_iteratorNormalCompletion = (_step = _iterator.next()).done)) return [
                        3,
                        5
                    ];
                    interceptor = _step.value;
                    return [
                        4,
                        interceptor(config)
                    ];
                case 3:
                    config = _state.sent();
                    _state.label = 4;
                case 4:
                    _iteratorNormalCompletion = true;
                    return [
                        3,
                        2
                    ];
                case 5:
                    return [
                        3,
                        8
                    ];
                case 6:
                    err = _state.sent();
                    _didIteratorError = true;
                    _iteratorError = err;
                    return [
                        3,
                        8
                    ];
                case 7:
                    try {
                        if (!_iteratorNormalCompletion && _iterator.return != null) {
                            _iterator.return();
                        }
                    } finally{
                        if (_didIteratorError) {
                            throw _iteratorError;
                        }
                    }
                    return [
                        7
                    ];
                case 8:
                    prefetches = getPrefetches();
                    if (prefetches.find(function(other) {
                        return isSameRequest(other.request, config);
                    })) {
                        return [
                            2
                        ];
                    }
                    handler = getGlobalProperty("faire.api.handler");
                    if (!handler) {
                        logError("Prefetch attempted when no requestHandler exists");
                        return [
                            2
                        ];
                    }
                    prefetch = {
                        sentAtMillis: Date.now(),
                        request: _object_spread_props(_object_spread({}, config), {
                            route: route,
                            beforePrefetchStartPerformanceMarker: beforePrefetchStartPerformanceMarker,
                            afterPrefetchDonePerformanceMarker: afterPrefetchDonePerformanceMarker
                        }),
                        response: handler(_object_spread_props(_object_spread({}, config), {
                            prefetch: true
                        }))
                    };
                    prefetches.push(prefetch);
                    ensurePrefetchConsumedWithin(prefetch, PREFETCH_UNUSED_TIMEOUT_MILLIS);
                    return [
                        2
                    ];
            }
        });
    });
    return function prefetch(requestOptions) {
        return _ref.apply(this, arguments);
    };
}();
var getPrefetches = function() {
    var globalScope = getWindowOrThrow();
    var faire = globalScope.faire || (globalScope.faire = {});
    var api = faire.api || (faire.api = {});
    return api.prefetches || (api.prefetches = []);
};
/**
 * Sets a timeout for any pending prefetches
 */ export var ensurePrefetchesConsumedWithin = function(timeoutMillis) {
    var _iteratorNormalCompletion = true, _didIteratorError = false, _iteratorError = undefined;
    try {
        for(var _iterator = getPrefetches()[Symbol.iterator](), _step; !(_iteratorNormalCompletion = (_step = _iterator.next()).done); _iteratorNormalCompletion = true){
            var prefetch = _step.value;
            ensurePrefetchConsumedWithin(prefetch, timeoutMillis);
        }
    } catch (err) {
        _didIteratorError = true;
        _iteratorError = err;
    } finally{
        try {
            if (!_iteratorNormalCompletion && _iterator.return != null) {
                _iterator.return();
            }
        } finally{
            if (_didIteratorError) {
                throw _iteratorError;
            }
        }
    }
};
var ensurePrefetchConsumedWithin = function(prefetch, timeoutMillis) {
    var _prefetch_response;
    var timeout = (_prefetch_response = prefetch.response) === null || _prefetch_response === void 0 ? void 0 : _prefetch_response.then(function() {
        return delay(timeoutMillis);
    });
    if (!timeout) {
        var limit = Math.max(prefetch.sentAtMillis + timeoutMillis - Date.now(), 0);
        timeout = delay(limit);
    }
    timeout.then(function() {
        return ensurePrefetchConsumed(prefetch);
    });
};
var ensurePrefetchConsumed = function(prefetch) {
    var samesiesIndex = getPrefetches().findIndex(function(other) {
        return isSameRequest(other.request, prefetch.request);
    });
    if (samesiesIndex >= 0) {
        // Remove from prefetches
        var samesies = getPrefetches().splice(samesiesIndex, 1);
        if (samesies) {
            logWarning("Prefetch ".concat(prefetch.request.method, " ").concat(prefetch.request.route, " was not used within timeout"), {
                data: {
                    prefetch: prefetch
                }
            });
            trackFrontendApisPrefetchUnusedView(prefetch.request.route);
        }
    }
};
